// SkuChatDrawer — Gemini AI chat panel for 品番ルール CRUD (iter_138b).
//
// Opens as a right-side drawer on the 品番マスター settings page. Users type
// natural-language requests ("SZ + 年 + 連番でルール作って") and Gemini calls
// backend tools to list, explain, create, update, delete, or preview
// templates via /api/sku-chat/message.
//
// Kept intentionally minimal: single-column chat log + input, no streaming
// (endpoint is non-streaming), no session-list UI in v1.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Robot, PaperPlaneTilt, Sparkle, Trash, PencilSimple, MagnifyingGlass,
  ArrowsClockwise, CheckCircle, XCircle, Info, ChatCircleDots, FileXls,
} from '@phosphor-icons/react';
import SkuImportPanel from './SkuImportPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tool → icon + accent color for the action-result chip. Keeps the chat log
// scannable when the assistant executes multiple operations in a session.
const TOOL_META = {
  list_templates:    { icon: MagnifyingGlass, label: '一覧',   tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  explain_template:  { icon: Info,            label: '説明',   tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  preview_template:  { icon: Sparkle,         label: 'プレビュー', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  create_template:   { icon: Sparkle,         label: '作成',   tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  update_template:   { icon: PencilSimple,    label: '更新',   tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  delete_template:   { icon: Trash,           label: '削除',   tone: 'bg-rose-50 text-rose-700 border-rose-200' },
  suggest_template:  { icon: Sparkle,         label: '提案',   tone: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' },
};

const EXAMPLE_PROMPTS = [
  'どんな品番ルールがある？',
  'IT機器用に、IT + 年下2桁 + 5桁連番のルール作って',
  'shizai_consumables を説明して',
  'shizai_consumables で次に発番される品番は？',
];

export function SkuChatDrawer({ open, onOpenChange, onTemplatesChanged }) {
  const [mode, setMode] = useState('chat'); // 'chat' | 'import'
  const [messages, setMessages] = useState([]); // {role, content, action?, action_result?, ts}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [modelLabel, setModelLabel] = useState('Gemini');
  const scrollRef = useRef(null);

  // Load model label + reset log on drawer open
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/sku-chat/config`, { withCredentials: true })
      .then((r) => setModelLabel(r.data?.model_label || 'Gemini'))
      .catch(() => {});
  }, [open]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((p) => [...p, { role: 'user', content: msg, ts: Date.now() }]);
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/sku-chat/message`,
        { message: msg, session_id: sessionId },
        { withCredentials: true, timeout: 90000 },
      );
      const d = r.data;
      if (!sessionId && d.session_id) setSessionId(d.session_id);
      setMessages((p) => [...p, {
        role: 'assistant',
        content: d.reply || '',
        action: d.action,
        action_result: d.action_result,
        error: d.error,
        ts: Date.now(),
      }]);
      // Notify parent so the rule list refreshes after any write action
      if (d.action?.tool && ['create_template', 'update_template', 'delete_template'].includes(d.action.tool)) {
        onTemplatesChanged?.();
      }
      if (d.error) toast.error(d.error);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      setMessages((p) => [...p, {
        role: 'assistant',
        content: '',
        error: `AI応答エラー: ${detail}`,
        ts: Date.now(),
      }]);
      toast.error(`AI応答エラー: ${detail}`);
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId, onTemplatesChanged]);

  const clearSession = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col gap-0 p-0" data-testid="sku-chat-drawer">
        <SheetHeader className="border-b bg-teal-700 text-white p-4">
          <SheetTitle className="flex items-center gap-2 text-white">
            <Robot weight="duotone" size={22} />
            <span>AIアシスタント · 品番ルール</span>
          </SheetTitle>
          <SheetDescription className="text-white/85 text-xs">
            {modelLabel} — 自然な日本語で「作って」「削除して」「一覧」など指示できます
          </SheetDescription>
          {/* Mode toggle — chat vs. file import */}
          <div className="mt-3 flex gap-1 p-1 bg-white/15 rounded-lg" data-testid="sku-chat-mode-toggle">
            <button
              type="button"
              onClick={() => setMode('chat')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mode === 'chat' ? 'bg-white text-indigo-700 shadow' : 'text-white/85 hover:bg-white/10'
              }`}
              data-testid="sku-chat-mode-chat"
            >
              <ChatCircleDots size={13} weight={mode === 'chat' ? 'fill' : 'regular'} />
              チャット
            </button>
            <button
              type="button"
              onClick={() => setMode('import')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mode === 'import' ? 'bg-white text-indigo-700 shadow' : 'text-white/85 hover:bg-white/10'
              }`}
              data-testid="sku-chat-mode-import"
            >
              <FileXls size={13} weight={mode === 'import' ? 'fill' : 'regular'} />
              ファイル取り込み
            </button>
          </div>
        </SheetHeader>

        {mode === 'import' ? (
          <SkuImportPanel onImported={onTemplatesChanged} />
        ) : (
        <>
        {/* Log */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50" data-testid="sku-chat-log">
          {messages.length === 0 && !busy && (
            <div className="space-y-3">
              <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-white p-4 text-center">
                <Robot weight="duotone" size={32} className="mx-auto text-indigo-500 opacity-70" />
                <div className="mt-2 text-sm font-semibold text-zinc-800">AIに何でも聞いてみて</div>
                <div className="mt-1 text-xs text-zinc-500">
                  ルールの一覧・作成・更新・削除・プレビューが可能です
                </div>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 px-1">
                例
              </div>
              {EXAMPLE_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => send(p)}
                  disabled={busy}
                  data-testid={`sku-chat-example-${p.slice(0, 12)}`}
                  className="w-full text-left rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {messages.map((m, i) => {
            if (m.role === 'user') {
              return (
                <div key={i} className="flex justify-end" data-testid={`sku-chat-user-${i}`}>
                  <div className="max-w-[85%] rounded-lg rounded-tr-md bg-indigo-600 text-white px-3.5 py-2 text-sm whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              );
            }
            const meta = m.action?.tool ? TOOL_META[m.action.tool] : null;
            const ToolIcon = meta?.icon;
            const isError = !!m.error;
            const resultOk = m.action_result && !m.action_result.error;
            return (
              <div key={i} className="flex justify-start" data-testid={`sku-chat-assistant-${i}`}>
                <div className="max-w-[92%] space-y-2">
                  {m.content && (
                    <div className="rounded-lg rounded-tl-md bg-white border border-zinc-200 px-3.5 py-2 text-sm whitespace-pre-wrap text-zinc-800">
                      {m.content}
                    </div>
                  )}
                  {meta && (
                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${meta.tone}`}>
                      {ToolIcon && <ToolIcon size={11} weight="fill" />}
                      <span>{meta.label}</span>
                      {resultOk && <CheckCircle size={11} weight="fill" className="text-emerald-500" />}
                      {(isError || m.action_result?.error) && <XCircle size={11} weight="fill" className="text-rose-500" />}
                    </div>
                  )}
                  {m.action_result && (
                    <div className="rounded-lg border border-zinc-200 bg-white/60 p-2 text-[11px] font-mono text-zinc-600 max-h-56 overflow-y-auto">
                      {m.action_result.error ? (
                        <span className="text-rose-600">{m.action_result.error}</span>
                      ) : m.action?.tool === 'list_templates' && m.action_result.templates ? (
                        <div className="space-y-0.5">
                          {m.action_result.count === 0 && <div className="text-zinc-400">（該当なし）</div>}
                          {(m.action_result.templates || []).map((t) => (
                            <div key={t.code} className="flex items-center gap-2">
                              <span className="font-bold text-zinc-800">{t.name}</span>
                              <span className="text-zinc-500">{t.code}</span>
                              {t.sample && <span className="text-blue-600">→ {t.sample}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words">{JSON.stringify(m.action_result, null, 2)}</pre>
                      )}
                    </div>
                  )}
                  {isError && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {m.error}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {busy && (
            <div className="flex justify-start" data-testid="sku-chat-thinking">
              <div className="rounded-lg rounded-tl-md bg-white border border-zinc-200 px-3.5 py-2 text-sm text-zinc-500 flex items-center gap-2">
                <ArrowsClockwise className="animate-spin" size={14} />
                <span>考えています…</span>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t bg-white p-3 space-y-2">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={clearSession}
              className="text-[10px] text-zinc-400 hover:text-zinc-600 underline"
              data-testid="sku-chat-clear"
            >
              会話をリセット
            </button>
          )}
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="例: 資材消耗品みたいなルールをオフィス備品用に作って"
              disabled={busy}
              rows={2}
              className="resize-none text-sm"
              data-testid="sku-chat-input"
            />
            <Button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="bg-teal-700 hover:bg-teal-800 shrink-0"
              data-testid="sku-chat-send"
            >
              <PaperPlaneTilt weight="fill" size={16} />
            </Button>
          </div>
          <div className="text-[10px] text-zinc-400 text-center">
            Enter で送信 · Shift+Enter で改行
          </div>
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default SkuChatDrawer;
